<template>
  <transition
    enter-active-class="transition-opacity duration-300"
    leave-active-class="transition-opacity duration-300"
    enter-from-class="opacity-0"
    enter-to-class="opacity-100"
    leave-from-class="opacity-100"
    leave-to-class="opacity-0"
  >
    <div v-if="show" class="fixed inset-0 bg-black bg-opacity-50 z-50 p-8">
      <div class="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full h-full flex">
        <!-- Left Sidebar -->
        <div class="w-80 border-r dark:border-gray-700 flex flex-col">
          <!-- Search -->
          <div class="p-4 border-b dark:border-gray-700">
            <div class="relative">
              <input 
                v-model="searchQuery"
                type="text"
                placeholder="Search tutorials..."
                class="w-full pl-10 pr-4 py-2 bg-gray-100 dark:bg-gray-800 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
              >
              <i class="fas fa-search absolute left-3 top-3 text-gray-400"></i>
            </div>
          </div>

          <!-- Tutorial Categories -->
          <div class="flex-1 overflow-y-auto">
            <div v-for="category in filteredCategories" :key="category.id">
              <button
                @click="activeCategory = category.id"
                class="w-full p-4 flex items-center gap-3 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors relative"
                :class="activeCategory === category.id ? 'bg-gray-100 dark:bg-gray-800' : ''"
              >
                <!-- Category Icon -->
                <div 
                  class="w-12 h-12 rounded-full flex items-center justify-center"
                  :class="activeCategory === category.id ? 'bg-pink-100 text-pink-500' : 'bg-gray-100 text-gray-500'"
                >
                  <i :class="['fas', category.icon, 'text-xl']"></i>
                </div>
                
                <!-- Category Info -->
                <div class="flex-1 text-left">
                  <div class="font-medium dark:text-white">{{ category.label }}</div>
                  <div class="text-sm text-gray-500">{{ category.description }}</div>
                </div>

                <!-- Active Indicator -->
                <div 
                  v-if="activeCategory === category.id"
                  class="absolute left-0 top-0 bottom-0 w-1 bg-pink-500"
                ></div>
              </button>
            </div>
          </div>
        </div>

        <!-- Main Content Area -->
        <div class="flex-1 flex flex-col overflow-hidden">
          <!-- Header -->
          <div class="flex items-center justify-between p-6 border-b dark:border-gray-700">
            <div>
              <h2 class="text-2xl font-bold dark:text-white">
                {{ currentCategory?.label }}
              </h2>
              <p class="text-gray-500 dark:text-gray-400">
                {{ currentCategory?.description }}
              </p>
            </div>
            <button 
              @click="$emit('close')"
              class="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
            >
              <i class="fas fa-times text-xl"></i>
            </button>
          </div>

          <!-- Tutorial Content -->
          <div class="flex-1 overflow-y-auto p-6">
            <router-view></router-view>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup>
import { ref, computed } from 'vue'

const props = defineProps({
  show: Boolean
})

defineEmits(['close'])

const searchQuery = ref('')
const activeCategory = ref('getting-started')

const categories = [
  { 
    id: 'getting-started', 
    label: 'Getting Started', 
    icon: 'fa-play-circle',
    description: 'Learn the basics of OddsComposer'
  },
  { 
    id: 'game-management', 
    label: 'Game Management', 
    icon: 'fa-gamepad',
    description: 'Manage games and fixtures'
  },
  { 
    id: 'odds-composition', 
    label: 'Odds Composition', 
    icon: 'fa-chart-line',
    description: 'Master odds composition tools'
  },
  { 
    id: 'client-management', 
    label: 'Client Management', 
    icon: 'fa-users-cog',
    description: 'Handle client accounts and settings'
  },
  { 
    id: 'reports-analytics', 
    label: 'Reports & Analytics', 
    icon: 'fa-chart-bar',
    description: 'Analyze performance and data'
  }
]

const filteredCategories = computed(() => {
  if (!searchQuery.value) return categories
  const query = searchQuery.value.toLowerCase()
  return categories.filter(category => 
    category.label.toLowerCase().includes(query) ||
    category.description.toLowerCase().includes(query)
  )
})

const currentCategory = computed(() => 
  categories.find(c => c.id === activeCategory.value)
)
</script> 